import React, { useEffect, useCallback, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Button, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router";

import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import DocumentMoreInfo from "./components/documentViewMoreInfo";
import { DocumentFeedItem } from "../../models";
import DocumentShareButton from "../document-feed/components/documentShareButton";
import useQueryParams, { QUERY_PARAM_KEYS } from "modules/common/hooks/useQueryParams";
import useIsMounted from "modules/common/hooks/useIsMounted";

import "../../styles/documentViews.sass";

export const DocumentView: React.FunctionComponent<PropsWithRedux> = (props) => {
    const queryParams = useQueryParams();
    const history = useHistory();
    const { onDownloadDocument, view, onViewDocument, onClose } = props;
    const [isAutoDownloading, setIsAutoDownloading] = useState<boolean>(false);
    const isMounted = useIsMounted();

    const canPreview = useCallback(
        (): boolean =>
            view?.fileType === "jpeg" ||
            view?.fileType === "jpg" ||
            view?.fileType === "pdf" ||
            view?.fileType === "png" ||
            view?.fileType === "txt",
        [view]
    );

    const handlePreview = useCallback(
        (target: "_blank" | "_self" = "_blank") => {
            if (view) {
                onViewDocument(view, target);
                onClose();
            }
        },
        [view, onViewDocument, onClose]
    );

    useEffect(() => {
        const removeAutoDownloadQueryParam = () => {
            queryParams.delete(QUERY_PARAM_KEYS.AUTO_DOWNLOAD);
            history.replace({ search: queryParams.toString() });
        };

        // either preview in same tab if extension is previewable,
        // or download file
        const onAutoDownload = async () => {
            if (!view) return;

            if (canPreview()) {
                handlePreview("_self");
            } else {
                setIsAutoDownloading(true);
                await onDownloadDocument(view);

                if (isMounted()) {
                    setIsAutoDownloading(false);
                }
            }
        };

        const autoDownload = queryParams.get(QUERY_PARAM_KEYS.AUTO_DOWNLOAD);
        if (autoDownload !== null && autoDownload === "true" && view) {
            removeAutoDownloadQueryParam();
            onAutoDownload();
        }
    }, [queryParams, history, view, onDownloadDocument, canPreview, handlePreview, isMounted]);

    const handleDownload = () => {
        if (view) {
            onDownloadDocument(view);
            onClose();
        }
    };

    const getPreviewButton = (): JSX.Element => {
        if (!canPreview()) return <></>;

        return (
            <Button
                onClick={() => handlePreview()}
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                style={{ marginRight: "10px" }}
            >
                PREVIEW
            </Button>
        );
    };

    const getDialogFooter = (): JSX.Element => (
        <DialogActions className="action-section doc-footer">
            <DocumentShareButton documentId={view?.id ?? ""}>COPY LINK</DocumentShareButton>

            <div>
                {getPreviewButton()}
                <Button disabled={isAutoDownloading} onClick={handleDownload} variant="contained" startIcon={<FileDownloadOutlinedIcon />}>
                    DOWNLOAD
                </Button>
            </div>
        </DialogActions>
    );

    const getDialogHeader = (): JSX.Element => (
        <DialogTitle>
            <div className="action-section">
                <Typography variant="h2" marginY="auto">
                    {" "}
                    More Information{" "}
                </Typography>
                <IconButton onClick={onClose} size="large">
                    <CloseIcon />
                </IconButton>
            </div>
        </DialogTitle>
    );

    if (!view) return <React.Fragment />;
    return (
        <>
            <Dialog open={props.show} onClose={onClose} classes={{ paper: "document-view-paper" }} className="document-view">
                {getDialogHeader()}

                <DialogContent>
                    <DocumentMoreInfo view={view} />
                </DialogContent>

                {getDialogFooter()}
                <LoadingOverlay show={props.isSaving || props.isUploading} styles={{ paddingTop: 150 }} />
            </Dialog>
            <Snackbar
                open={!!props.errorMessage}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                autoHideDuration={6000}
                onClose={props.clearErrorMessage}
            >
                <Alert variant="filled" severity="error" elevation={6} onClose={props.clearErrorMessage}>
                    {props.errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

interface ComponentProps {
    show: boolean;
    view: DocumentFeedItem | undefined;
    onDownloadDocument: (document: DocumentFeedItem) => Promise<void>;
    onViewDocument: (document: DocumentFeedItem, target: "_blank" | "_self") => Promise<void>;
    onClose: () => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        tenantId: state.tenant.id,
        errorMessage: state.documents.errorMessage,
        isSaving: state.documents.isSaving,
        isUploading: state.documents.isUploading,
    }),
    {
        clearErrorMessage: actions.clearErrorMessage,
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(DocumentView);
